import type {
  ConnectionConnectorGroup,
  ConnectionFacets,
  ConnectorDefinitionItem,
} from "@/lib/api/connections";

/**
 * Every filter chip a connector list can offer. Each describes a connector
 * capability. The auth method a connector uses (OAuth / API key / app install)
 * stays out of this vocabulary because it is setup detail, not what the
 * connector can do.
 */
export type ConnectorFilter =
  | "all"
  | "data"
  | "chat"
  | "actions"
  | "inference"
  | "runtime"
  | "device";

/** The canonical chip label for every filter. */
export const CONNECTOR_FILTER_LABELS: Record<ConnectorFilter, string> = {
  all: "All",
  data: "Data",
  chat: "Chat",
  actions: "Actions",
  inference: "Inference",
  runtime: "Runtime",
  device: "On device",
};

/** Stable URL vocabulary for the connectors list's `?filter=` parameter. */
export const CONNECTOR_FILTERS: readonly ConnectorFilter[] = [
  "all",
  "data",
  "chat",
  "actions",
  "inference",
  "runtime",
  "device",
];

export function parseConnectorFilter(
  value: unknown
): ConnectorFilter | undefined {
  return typeof value === "string" &&
    CONNECTOR_FILTERS.includes(value as ConnectorFilter)
    ? (value as ConnectorFilter)
    : undefined;
}

/**
 * A connector declares itself chat-capable via the `x-lobu-chat-platform`
 * marker in its `options_schema` (slack/telegram/discord/… all set it). This is
 * the DEFINITION-level signal — the same one the server reads at connect time —
 * so an uninstalled chat connector can advertise chat before any connection
 * exists. Catalog use only: per-connection `facets.chat` stays live-only (it
 * means "a live bot adapter", which the run-on-device picker keys off).
 */
function connectorDeclaresChat(
  connector: ConnectorDefinitionItem
): boolean {
  return connector.options_schema?.["x-lobu-chat-platform"] != null;
}

/** Whether a connector runs on a device (needs a local runtime to execute). */
export function connectorIsDeviceBound(
  connector: ConnectorDefinitionItem
): boolean {
  return !!connector.runtime || !!connector.required_capability;
}

/**
 * Derive a connector's capability facets. A live connection group's facets win
 * (they reflect actual configured state); otherwise fall back to what the
 * connector definition declares, so an uninstalled connector still advertises
 * what it CAN do (data/chat/actions).
 */
export function deriveConnectorFacets(
  connector: ConnectorDefinitionItem,
  group?: ConnectionConnectorGroup
): ConnectionFacets {
  const feeds = connector.feeds_schema
    ? Object.keys(connector.feeds_schema).length > 0
    : false;
  const actions =
    connector.has_operations ||
    (connector.actions_schema
      ? Object.keys(connector.actions_schema).length > 0
      : false);
  return {
    data: group?.facets.data ?? feeds,
    chat: group?.facets.chat ?? connectorDeclaresChat(connector),
    actions: group?.facets.actions ?? actions,
    audience: group?.facets.audience ?? false,
    // A connector definition never serves inference or runtime — model
    // providers and sandboxes live in their own tables and are synthesized into
    // the list as their own rows (see the connectors index). Nothing derived
    // from a connector can flip either.
    inference: false,
    runtime: false,
  };
}

/** The connector + derived state a filter is evaluated against. */
interface ConnectorFilterItem {
  connector: ConnectorDefinitionItem;
  facets: ConnectionFacets;
  /** Installed-but-unconfigured. Optional — catalog entries have no setup state. */
  needsSetup?: boolean;
  /**
   * The row IS a device, rather than a connector bound to one. A device row
   * carries neither `runtime` nor `required_capability` — it is the thing
   * those fields point AT — so without this flag "On device" listed Screen
   * Time and Photos but not a single actual device.
   */
  isDevice?: boolean;
}

/**
 * The single matcher both connector lists use — every filter is a capability
 * facet, and a row matches when it advertises that capability.
 *
 * Setup state is deliberately NOT a filter. Connectors needing setup already
 * sort to the top of the list and carry their own row badge, so a chip only
 * offered a second, less discoverable route to what the default order shows.
 */
export function connectorMatchesFilter(
  item: ConnectorFilterItem,
  filter: ConnectorFilter
): boolean {
  const { connector, facets } = item;
  if (filter === "all") return true;
  if (filter === "data") return facets.data;
  if (filter === "chat") return facets.chat;
  if (filter === "actions") return facets.actions;
  if (filter === "inference") return facets.inference;
  if (filter === "runtime") return facets.runtime === true;
  return item.isDevice === true || connectorIsDeviceBound(connector);
}
